import React, { useState, useEffect } from 'react';
import { apiUrl } from '../../ApiHelper/ApiUrl';
import {
    ArrowLeft, FileIcon, Eye, CheckCircle2, ShieldCheck,
    MessageSquare, AlertTriangle, Lightbulb, Clock, DownloadCloud,
    FolderOpen, X, RefreshCw, Activity, MessageCircle, StickyNote, Layers, Target, Trash2, Save, Edit3, Shield, ChevronDown, Plus, UserCheck
} from 'lucide-react';
import FilePreviewModal from '../../components/FilePreviewModal';
import NoteSection from './NoteSection';
import CommentSection from './CommentSection';
import Card from '../../components/card';
import CardHeader from '../../components/cardHeader';
import LanguageSelector from './LanguageSelector';
import { useDispatch } from 'react-redux';
import { fetchEvaluationDetails } from '../../redux/features/evaluation/evaluationSlice';

// --- MAIN COMPONENT ---
const ControlDetailView = ({
    setPageIsLoading, pageIsLoading, control, evidence, remarks,
    onBack, onPreviewFile, currentUser, onStatusChange, onAddRemark,
    mockControlsInitial, handleFetchRequest, RequestChangesModal,
    isPreviewModalOpen, setIsPreviewModalOpen, file
}) => {
    const [activeTab, setActiveTab] = useState("notes");
    const [isRemarkModalOpen, setIsRemarkModalOpen] = useState(false);
    const frameworkId = Object.keys(mockControlsInitial).find(key => mockControlsInitial[key].some(c => c.id === control.id));
    const [evaluationDetails, setEvaluationDetails] = useState({});
    const [isApproved, setIsApproved] = useState(false);
    const [selectedLang, setSelectedLang] = useState('English');

    const [localStatus, setLocalStatus] = useState(control?.status || 'In review');
    const [pendingStatusChange, setPendingStatusChange] = useState(null);

    // NEW: State for Gaps Filter (Internal removed)
    const [gapFilter, setGapFilter] = useState('All');
    const dispatch = useDispatch();

    useEffect(() => {
        if (control?.status) {
            setLocalStatus(control.status);
        }
    }, [control?.status]);

    const [newComment, setNewComment] = useState("");
    const [comments, setComments] = useState([
        { id: 1, text: "Please make sure to upload the logs from Q3 as well. The current file only covers Q2.", user: { name: "Cynthia Patel", role: "Admin", avatarUrl: "https://i.pravatar.cc/150?u=a042581f4e29026704d" }, timestamp: new Date(Date.now() - 86400000).toISOString() },
        { id: 2, text: "Got it, I will pull the Q3 logs from the AWS console and upload them by tomorrow.", user: { name: "Ben Carter", role: "User", avatarUrl: "https://i.pravatar.cc/150?u=a042581f4e29026702d" }, timestamp: new Date(Date.now() - 3600000).toISOString() }
    ]);

    const [newNote, setNewNote] = useState("");
    const [notes, setNotes] = useState([
        { id: 1, text: "Need to cross-reference this control with the new GDPR guidelines before final approval. Check section 4.1.", timestamp: new Date(Date.now() - 172800000).toISOString() },
        { id: 2, text: "Follow up with IT DevOps regarding the outdated access matrix. Need this resolved by Friday.", timestamp: new Date(Date.now() - 86400000).toISOString() }
    ]);
    const [noteToDelete, setNoteToDelete] = useState(null);

    // --- MODIFIED: Internal Gaps removed. ---
    const [gaps, setGaps] = useState([
        {
            id: "GAP-1031", title: "Missing MFA Evidence", description: "Screenshot of MFA configuration for admin accounts was not included in the primary upload.", severity: "Critical", assignTo: "Security Team", dueDate: "2024-04-10", status: "Resolved", type: "auditor", auditorName: "Sarah Jenkins (External)"
        }
    ]);
    const [showGapForm, setShowGapForm] = useState(false);
    const [editingGapId, setEditingGapId] = useState(null);
    const [gapData, setGapData] = useState({
        title: '', description: '', severity: 'Medium', assignTo: '', dueDate: '', status: 'Open', type: 'auditor', auditorName: ''
    });

    const handleApprove = () => {
        const newStatus = 'Passed';
        setLocalStatus(newStatus);
        onStatusChange(frameworkId, newStatus);
        onAddRemark('Control approved.');
        setIsApproved(true);
    };

    const handleRequestChanges = (remarkText) => {
        const newStatus = 'Failed';
        setLocalStatus(newStatus);
        onStatusChange(frameworkId, newStatus);
        onAddRemark(remarkText);
        setIsRemarkModalOpen(false);
    };

    const handleStatusDropdownChange = (e) => {
        const newStatus = e.target.value;
        if (newStatus !== localStatus) {
            setPendingStatusChange(newStatus);
        }
    };

    const confirmStatusChange = () => {
        if (pendingStatusChange) {
            setLocalStatus(pendingStatusChange);
            onStatusChange(frameworkId, pendingStatusChange);
            setPendingStatusChange(null);
        }
    };

    const cancelStatusChange = () => {
        setPendingStatusChange(null);
    };

    const getEvaluationDetails = async () => {
        setPageIsLoading(true);
        try {
            const result = await dispatch(fetchEvaluationDetails(evidence?.evaluationId)).unwrap();
            if(result?.status) {
                setEvaluationDetails(result?.data);
            }
            // handleFetchRequest("evaluation", `${apiUrl.evaluation_details}/${evidence?.evaluationId}`).then((response) => {
            //     if (response?.status === true) {
            //         setEvaluationDetails(response?.data);
            //     }
            //     setPageIsLoading(false);
            // });
            
        } catch (error) {
            console.log("##error", error);
        } finally {
            setPageIsLoading(false);
        }
    }

    useEffect(() => {
        if (evidence?.evaluationId) {
            getEvaluationDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [evidence?.evaluationId]);

    const handleSendComment = () => {
        if (!newComment.trim()) return;
        const commentObj = { id: Date.now(), text: newComment, user: currentUser || { name: 'Current User', role: 'User', avatarUrl: 'https://i.pravatar.cc/150?u=placeholder' }, timestamp: new Date().toISOString() };
        setComments([...comments, commentObj]);
        setNewComment("");
    };

    const handleSaveNote = () => {
        if (!newNote.trim()) return;
        const noteObj = { id: Date.now(), text: newNote, timestamp: new Date().toISOString() };
        setNotes([noteObj, ...notes]);
        setNewNote("");
    };

    const confirmDeleteNote = (id) => setNoteToDelete(id);
    const executeDeleteNote = () => {
        if (noteToDelete) {
            setNotes(notes.filter(note => note.id !== noteToDelete));
            setNoteToDelete(null);
        }
    };

    // --- GAP HANDLERS ---
    const handleEditGapClick = (gap) => {
        setGapData({ ...gap });
        setEditingGapId(gap.id);
        setShowGapForm(true);
    };

    const handleGapSubmit = (e) => {
        e.preventDefault();
        if (editingGapId) {
            setGaps(gaps.map(g => g.id === editingGapId ? { ...gapData, id: editingGapId } : g));
            setEditingGapId(null);
        } else {
            const newGap = { ...gapData, id: `GAP-${Math.floor(1000 + Math.random() * 9000)}`, status: "Open" };
            setGaps([newGap, ...gaps]);
        }
        setGapData({ title: '', description: '', severity: 'Medium', assignTo: '', dueDate: '', status: 'Open', type: 'auditor', auditorName: '' });
        setShowGapForm(false);
    };

    const handleCancelGapForm = () => {
        setShowGapForm(false);
        setEditingGapId(null);
        setGapData({ title: '', description: '', severity: 'Medium', assignTo: '', dueDate: '', status: 'Open', type: 'auditor', auditorName: '' });
    };

    const getStatusTheme = (status) => {
        switch (status?.toLowerCase()) {
            case 'passed': case 'passing': return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
            case 'failed': case 'failing': return 'bg-rose-50 text-rose-700 ring-rose-200';
            case 'needs more evidence/gap raised': return 'bg-amber-50 text-amber-700 ring-amber-200';
            case 'in review': default: return 'bg-blue-50 text-blue-700 ring-blue-200';
        }
    };

    // --- Derived states for Gap Rendering ---
    const auditorGaps = gaps.filter(g => g.type === 'auditor');
    const aiGaps = (evaluationDetails?.gaps || []).map((gapStr, idx) => ({
        id: `AI-GAP-${idx + 1}`,
        title: "AI Evaluated Finding",
        description: gapStr,
        severity: "Medium",
        assignTo: "Pending Review",
        dueDate: "N/A",
        status: "Open",
        type: "ai"
    }));

    // Visibility logic for Gaps Filter
    const showAISection = (gapFilter === 'All' && aiGaps.length > 0) || gapFilter === 'AI';
    const showAuditorSection = (gapFilter === 'All' && auditorGaps.length > 0) || gapFilter === 'Auditor';

    const selectedResult = (evaluationDetails?.results ?? [])?.find(
        (r) => r.language === selectedLang
    );
    const displayData = selectedResult?.data || evaluationDetails;

    // Helper to render gap cards
    const renderGapCard = (gap, isEditable = true) => (
        <div key={gap.id} className={`bg-white p-5 rounded-2xl ring-1 shadow-sm hover:shadow-md transition-all group ${gap.type === 'ai' ? 'ring-emerald-200/60 hover:ring-emerald-300' : 'ring-slate-200/60 hover:ring-indigo-200'}`}>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                    <span className={`text-xs font-black px-2 py-1 rounded-md ${gap.type === 'ai' ? 'text-emerald-600 bg-emerald-50' : 'text-slate-500 bg-slate-100'}`}>
                        {gap.id}
                    </span>
                    <h5 className="font-bold text-slate-900">{gap.title}</h5>
                </div>
                <div className="flex items-center gap-3">
                    <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black tracking-widest uppercase ring-1 ${
                        gap.status === 'Open' ? 'bg-amber-50 text-amber-700 ring-amber-200' : 
                        gap.status === 'In Review' ? 'bg-blue-50 text-blue-700 ring-blue-200' :
                        gap.status === 'Resolved' ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' :
                        'bg-slate-50 text-slate-700 ring-slate-200'
                    }`}>
                        {gap.status}
                    </span>
                    {/* Only show edit button for auditor gaps */}
                    {isEditable && (
                        <button onClick={() => handleEditGapClick(gap)} className="p-1.5 text-slate-400 hover:text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors " title="Edit Risk">
                            <Edit3 size={16} strokeWidth={2.5} />
                        </button>
                    )}
                </div>
            </div>
            <p className="text-sm text-slate-600 mb-4">{gap.description}</p>
            <div className={`flex flex-wrap items-center gap-4 text-xs font-medium p-3 rounded-xl ring-1 ${gap.type === 'ai' ? 'text-emerald-700 bg-emerald-50/50 ring-emerald-100' : 'text-slate-500 bg-slate-50 ring-slate-200/60'}`}>
                <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${gap.severity === 'Critical' ? 'bg-rose-500' : gap.severity === 'High' ? 'bg-orange-500' : gap.severity === 'Medium' ? 'bg-amber-400' : 'bg-emerald-400'}`}></span>
                    Severity: <span className="font-bold text-slate-700">{gap.severity}</span>
                </div>
                <div className="w-px h-3 bg-slate-300"></div>
                <div>Assign To: <span className="font-bold text-slate-700">{gap.assignTo}</span></div>
                <div className="w-px h-3 bg-slate-300"></div>
                <div>Due: <span className="font-bold text-slate-700">{gap.dueDate}</span></div>
                {gap.type === 'auditor' && gap.auditorName && (
                    <>
                        <div className="w-px h-3 bg-slate-300"></div>
                        <div className="flex items-center gap-1.5 text-indigo-700">
                            <UserCheck size={14}/> Auditor: <span className="font-bold">{gap.auditorName}</span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    return (
        <div className="animate-in fade-in zoom-in-[0.98] duration-400 ease-out max-w-[1600px] mx-auto">
            <div className="flex  items-center justify-between gap-4 mb-10">
                <div className='flex items-start gap-4'>
                    <button onClick={onBack} className="p-2.5 bg-white ring-1 ring-slate-200 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 hover:ring-indigo-200 rounded-xl transition-all shadow-sm" title="Back to Frameworks">
                        <ArrowLeft size={20} strokeWidth={2.5} />
                    </button>
                    <div>
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                            {control?.referenceCode && (
                                <span className="bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-lg text-xs font-black tracking-widest uppercase ring-1 ring-indigo-200/50 shadow-sm">{control.referenceCode}</span>
                            )}
                            <div className="relative group">
                                <select 
                                    value={localStatus}
                                    onChange={handleStatusDropdownChange}
                                    className={`appearance-none px-3 py-1.5 pr-8 rounded-lg text-xs font-black tracking-widest uppercase ring-1 shadow-sm cursor-pointer transition-all focus:outline-none focus:ring-2 ${getStatusTheme(localStatus)}`}
                                >
                                    <option value="In review">IN REVIEW</option>
                                    <option value="Passing">PASSED</option>
                                    <option value="Failed">FAILED</option>
                                    <option value="Needs more evidence/Gap raised">NEEDS MORE EVIDENCE / Risk RAISED</option>
                                </select>
                                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" />
                            </div>
                            <div className="relative group">
                                <LanguageSelector 
                                    onOk={(lang) => {
                                        setSelectedLang(lang?.value);
                                    }}
                                />
                            </div>
                        </div>
                        <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight flex items-center gap-3">{control.controlName || control.title}</h2>
                        <p className="text-sm font-semibold text-slate-500 mt-1">{control.description}</p>
                    </div>
                </div>
                <div>
                    <div className="bg-gradient-to-br from-indigo-500 to-blue-600 p-6 rounded-3xl shadow-lg flex flex-col justify-center items-center text-center text-white relative overflow-hidden">
                        <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full blur-xl pointer-events-none"></div>
                        <h5 className="font-bold text-xs text-indigo-100 uppercase tracking-widest mb-2 relative z-10">Confidence Score</h5>
                        <p className="text-4xl font-black tracking-tight relative z-10">{evaluationDetails.score || '-'}</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-8 flex flex-col gap-6">
                    {/* Modern Segmented Control */}
                    <div className="bg-white p-2 rounded-2xl ring-1 ring-slate-200/60 shadow-sm flex flex-wrap xl:flex-nowrap gap-2 w-full">
                        <button onClick={() => setActiveTab("evidence")} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl transition-all duration-300 whitespace-nowrap ${activeTab === "evidence" ? "bg-indigo-50 text-indigo-700 shadow-sm ring-1 ring-indigo-200/50" : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"}`}>
                            <FolderOpen size={16} /> Evidence
                        </button>
                        <button onClick={() => setActiveTab("evaluation")} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl transition-all duration-300 whitespace-nowrap ${activeTab === "evaluation" ? "bg-indigo-50 text-indigo-700 shadow-sm ring-1 ring-indigo-200/50" : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"}`}>
                            <Activity size={16} /> AI Evaluation
                        </button>
                        <button onClick={() => setActiveTab("comments")} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl transition-all duration-300 whitespace-nowrap ${activeTab === "comments" ? "bg-indigo-50 text-indigo-700 shadow-sm ring-1 ring-indigo-200/50" : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"}`}>
                            <MessageCircle size={16} /> Comments
                        </button>
                        <button onClick={() => setActiveTab("notes")} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl transition-all duration-300 whitespace-nowrap ${activeTab === "notes" ? "bg-indigo-50 text-indigo-700 shadow-sm ring-1 ring-indigo-200/50" : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"}`}>
                            <StickyNote size={16} /> Notes
                        </button>
                        <button onClick={() => { setActiveTab("gaps"); handleCancelGapForm(); setGapFilter('All'); }} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl transition-all duration-300 whitespace-nowrap ${activeTab === "gaps" ? "bg-indigo-50 text-indigo-700 shadow-sm ring-1 ring-indigo-200/50" : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"}`}>
                            <AlertTriangle size={16} /> Risks
                            {/* Gaps */}
                        </button>
                    </div>

                    <div className="bg-white rounded-[2rem] ring-1 ring-slate-200/60 shadow-sm p-6 sm:p-8 min-h-[400px] flex flex-col">
                        {/* TAB 1: EVIDENCE */}
                        {activeTab === "evidence" && (
                            evidence ? (
                                <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <div>
                                        <h5 className="flex items-center gap-2 font-black text-sm text-slate-800 uppercase tracking-wider mb-4"><MessageSquare size={18} className="text-indigo-500" /> Submitted Narrative</h5>
                                        <div className="bg-slate-50 p-6 rounded-2xl ring-1 ring-slate-200/60 shadow-inner relative">
                                            <div className="absolute top-4 right-4 text-6xl text-slate-200/50 font-serif leading-none rotate-180 pointer-events-none">"</div>
                                            <p className="text-base text-slate-700 leading-relaxed font-medium relative z-10 whitespace-pre-wrap">{evidence?.narrative || "No narrative provided by the assignee."}</p>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between mb-4">
                                            <h5 className="flex items-center gap-2 font-black text-sm text-slate-800 uppercase tracking-wider"><DownloadCloud size={18} className="text-indigo-500" /> Attached Documents</h5>
                                            <span className="text-xs font-bold text-slate-500 bg-slate-100 px-3 py-1.5 rounded-lg">{evidence?.files?.length || 0} Files</span>
                                        </div>
                                        {evidence?.files && evidence?.files.length > 0 ? (
                                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                {evidence?.files?.map((file, index) => (
                                                    <li key={index} className="group flex flex-col p-5 ring-1 ring-slate-200 rounded-2xl hover:ring-indigo-300 hover:shadow-lg hover:-translate-y-1 transition-all bg-white cursor-pointer" onClick={() => onPreviewFile(file)}>
                                                        <div className="flex items-start gap-4 mb-5">
                                                            <div className="p-3 bg-gradient-to-br from-indigo-50 to-blue-50 text-indigo-600 rounded-xl group-hover:scale-110 transition-transform shadow-sm ring-1 ring-indigo-100"><FileIcon className="w-6 h-6" /></div>
                                                            <div className="flex-1 min-w-0">
                                                                <span className="block text-sm font-bold text-slate-800 truncate mb-1" title={file.fileName}>{file.fileName}</span>
                                                                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Document</span>
                                                            </div>
                                                        </div>
                                                        <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-bold text-slate-600 bg-slate-50 ring-1 ring-slate-200 group-hover:bg-indigo-600 group-hover:text-white group-hover:ring-indigo-600 rounded-xl transition-colors"><Eye className="w-4 h-4" /> Preview</button>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className="text-center py-12 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200">
                                                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm ring-1 ring-slate-200"><FileIcon className="h-8 w-8 text-slate-300" /></div>
                                                <p className="text-sm font-bold text-slate-500">No documents attached to this evidence.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center">
                                    <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-5 ring-1 ring-slate-200 shadow-sm"><FolderOpen size={32} className="text-slate-300" /></div>
                                    <h4 className="text-xl font-black text-slate-800 mb-2">No Evidence Available</h4>
                                    <p className="text-sm font-medium text-slate-500 max-w-sm">Evidence has not been uploaded for this control yet.</p>
                                </div>
                            )
                        )}

                        {/* TAB 2: EVALUATION */}
                        {activeTab === "evaluation" && (
                            evidence ? (
                                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <div className="bg-slate-50 p-6 rounded-3xl ring-1 ring-slate-200/60 shadow-inner">
                                        <h5 className="font-black text-sm text-slate-800 uppercase tracking-wider mb-3 flex items-center gap-2"><ShieldCheck size={18} className="text-indigo-500" /> Evaluation Justification</h5>
                                        <p className="text-sm text-slate-600 font-medium leading-relaxed">{displayData?.justification || "No justification provided."}</p>
                                        {/* <p className="text-sm text-slate-600 font-medium leading-relaxed">{evaluationDetails.justification || "No justification provided."}</p> */}
                                    </div>
                                    <div className="grid grid-cols-1 gap-6">
                                        {/* {evaluationDetails.gaps && evaluationDetails.gaps.length > 0 && ( */}
                                        {displayData?.gaps && displayData?.gaps.length > 0 && (
                                            <div className="bg-white p-6 rounded-3xl ring-1 ring-rose-200 shadow-sm relative overflow-hidden">
                                                <div className="absolute top-0 left-0 w-1.5 h-full bg-rose-400"></div>
                                                <h5 className="font-black text-sm text-rose-700 uppercase tracking-wider mb-4 flex items-center gap-2"><AlertTriangle size={18} /> 
                                                    Identified Risks
                                                    {/* Identified Gaps */}
                                                </h5>
                                                <ul className="space-y-3">
                                                    {/* {evaluationDetails.gaps.map((item, idx) => ( */}
                                                    {displayData?.gaps.map((item, idx) => (
                                                        <li key={idx} className="text-sm font-medium text-slate-700 flex items-start gap-3">
                                                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-rose-400 shrink-0 shadow-[0_0_8px_rgba(244,63,94,0.6)]"></span><span className="leading-relaxed">{item}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {/* {evaluationDetails.recommendations && evaluationDetails.recommendations.length > 0 && ( */}
                                        {displayData?.recommendations && displayData?.recommendations.length > 0 && (
                                            <div className="bg-white p-6 rounded-3xl ring-1 ring-emerald-200 shadow-sm relative overflow-hidden">
                                                <div className="absolute top-0 left-0 w-1.5 h-full bg-emerald-400"></div>
                                                <h5 className="font-black text-sm text-emerald-700 uppercase tracking-wider mb-4 flex items-center gap-2"><Lightbulb size={18} /> Recommendations</h5>
                                                <ul className="space-y-3">
                                                    {/* {evaluationDetails.recommendations.map((item, idx) => ( */}
                                                    {displayData.recommendations.map((item, idx) => (
                                                        <li key={idx} className="text-sm font-medium text-slate-700 flex items-start gap-3">
                                                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0 shadow-[0_0_8px_rgba(16,185,129,0.6)]"></span><span className="leading-relaxed">{item}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center">
                                    <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-5 ring-1 ring-slate-200 shadow-sm"><Activity size={32} className="text-slate-300" /></div>
                                    <h4 className="text-xl font-black text-slate-800 mb-2">No Evaluation Data</h4>
                                    <p className="text-sm font-medium text-slate-500 max-w-sm">Upload evidence to trigger an AI evaluation.</p>
                                </div>
                            )
                        )}

                        {/* TAB 3: COMMENTS */}
                        {activeTab === "comments" && (<CommentSection comments={comments} newComment={newComment} setNewComment={setNewComment} onSendComment={handleSendComment} currentUser={currentUser} />)}

                        {/* TAB 4: PERSONAL NOTES */}
                        {activeTab === "notes" && (<NoteSection notes={notes} newNote={newNote} setNewNote={setNewNote} onSaveNote={handleSaveNote} onDeleteNote={confirmDeleteNote} currentUser={currentUser} />)}

                        {/* TAB 5: GAPS */}
                        {activeTab === "gaps" && (
                            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                                {!showGapForm ? (
                                    <>
                                        {/* Filter UI & Header */}
                                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-rose-100 text-rose-600 rounded-lg"><AlertTriangle size={20} strokeWidth={2.5} /></div>
                                                <h4 className="text-xl font-black text-slate-900 tracking-tight">                                                    
                                                    Compliance Risks
                                                    {/* Compliance Gaps */}
                                                </h4>
                                            </div>
                                            
                                            <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                                                <div className="flex bg-slate-100 p-1 rounded-xl ring-1 ring-slate-200/60 shadow-inner overflow-x-auto">
                                                    {['All', 'AI', 'Auditor'].map((filter) => (
                                                        <button
                                                            key={filter}
                                                            onClick={() => setGapFilter(filter)}
                                                            className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all whitespace-nowrap ${
                                                                gapFilter === filter 
                                                                    ? 'bg-white text-indigo-600 shadow-sm ring-1 ring-slate-200/50' 
                                                                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                                                            }`}
                                                        >
                                                            {filter}
                                                        </button>
                                                    ))}
                                                </div>
                                                <button onClick={() => setShowGapForm(true)} className="flex gap-2 items-center px-4 py-2 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-all shadow-md active:scale-95 shrink-0">
                                                    <Plus size={16} /> Raise Risk
                                                    {/* Gap */}
                                                </button>
                                            </div>
                                        </div>

                                        {(gaps.length > 0 || aiGaps.length > 0) ? (
                                            <div className="space-y-8 mt-4">
                                                
                                                {/* SECTION: AI Gaps */}
                                                {showAISection && (
                                                    <div>
                                                        <h5 className="text-sm font-bold text-emerald-500 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2 flex items-center gap-2">
                                                            <Activity size={16} className="text-emerald-400"/> AI Evaluated Risks
                                                            {/* AI Evaluated Gaps */}
                                                        </h5>
                                                        {aiGaps.length > 0 ? (
                                                            <div className="space-y-4">
                                                                {/* Passing false so edit button is hidden for AI gaps */}
                                                                {aiGaps.map(g => renderGapCard(g, false))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm font-medium text-slate-400 italic">                                                                
                                                                No AI evaluated Risks found.
                                                                {/* No AI evaluated gaps found. */}
                                                            </p>
                                                        )}
                                                    </div>
                                                )}

                                                {/* SECTION: Auditor Gaps */}
                                                {showAuditorSection && (
                                                    <div>
                                                        <h5 className="text-sm font-bold text-indigo-500 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2 flex items-center gap-2">
                                                            <Target size={16} className="text-indigo-400"/> 
                                                            Auditor Identified Risks
                                                            {/* Auditor Identified Gaps */}
                                                        </h5>
                                                        {auditorGaps.length > 0 ? (
                                                            <div className="space-y-4">
                                                                {auditorGaps.map(g => renderGapCard(g, true))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm font-medium text-slate-400 italic">
                                                                No auditor Risks logged.
                                                                {/* No auditor gaps logged. */}
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="text-center py-12 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200 mt-4">
                                                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm ring-1 ring-slate-200">
                                                    <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                                                </div>
                                                <p className="text-sm font-bold text-slate-500">
                                                    No Risks have been identified for this control.
                                                    {/* No gaps have been identified for this control. */}
                                                </p>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <div className="flex items-center justify-between gap-3 mb-6">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg"><Edit3 size={20} strokeWidth={2.5} /></div>
                                                <h4 className="text-lg font-black text-slate-900 tracking-tight">{editingGapId ? `Edit Risk ${editingGapId}` : 'Raise a New Risk'}</h4>
                                                
                                                {/* <h4 className="text-lg font-black text-slate-900 tracking-tight">{editingGapId ? `Edit Gap ${editingGapId}` : 'Raise a New Gap'}</h4> */}
                                            </div>
                                            <button onClick={handleCancelGapForm} className="flex gap-2 items-center px-4 py-2 bg-white text-slate-600 ring-1 ring-slate-200 text-sm font-bold rounded-xl hover:bg-slate-50 transition-all shadow-sm active:scale-95">
                                                <X size={16} /> Cancel
                                            </button>
                                        </div>
                                        
                                        <form onSubmit={handleGapSubmit} className="space-y-5 bg-slate-50 p-6 sm:p-8 rounded-3xl ring-1 ring-slate-200/60 shadow-inner">
                                            <div className="mb-5">
                                                <label className="block text-sm font-bold text-slate-700 mb-2">Auditor Name <span className="text-rose-500">*</span></label>
                                                <input type="text" className="w-full p-3.5 rounded-xl border-0 ring-1 ring-slate-300 focus:ring-2 focus:ring-indigo-500 bg-white shadow-sm transition-all text-sm font-medium text-slate-800 placeholder:text-slate-400" placeholder="e.g. EY - Sarah Jenkins" value={gapData.auditorName} onChange={e => setGapData({...gapData, auditorName: e.target.value})} required />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-bold text-slate-700 mb-2">
                                                    Risk Title                                                    
                                                    {/* Gap Title */}
                                                </label>
                                                <input type="text" className="w-full p-3.5 rounded-xl border-0 ring-1 ring-slate-300 focus:ring-2 focus:ring-indigo-500 bg-white shadow-sm transition-all text-sm font-medium text-slate-800 placeholder:text-slate-400" placeholder="Enter risk title..." value={gapData.title} onChange={e => setGapData({...gapData, title: e.target.value})} required />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-slate-700 mb-2">Description</label>
                                                <textarea className="w-full p-3.5 rounded-xl border-0 ring-1 ring-slate-300 focus:ring-2 focus:ring-indigo-500 bg-white shadow-sm transition-all min-h-[120px] text-sm font-medium text-slate-800 placeholder:text-slate-400 resize-y" placeholder="Describe the missing requirement or control failure..." value={gapData.description} onChange={e => setGapData({...gapData, description: e.target.value})} required />
                                            </div>
                                            
                                            <div className={`grid grid-cols-1 md:grid-cols-2 ${editingGapId ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-5`}>
                                                <div>
                                                    <label className="block text-sm font-bold text-slate-700 mb-2">Severity</label>
                                                    <div className="relative">
                                                        <select className="w-full p-3.5 pr-10 appearance-none rounded-xl border-0 ring-1 ring-slate-300 focus:ring-2 focus:ring-indigo-500 bg-white shadow-sm transition-all text-sm font-medium text-slate-800 cursor-pointer" value={gapData.severity} onChange={e => setGapData({...gapData, severity: e.target.value})}>
                                                            <option value="Low">Low</option>
                                                            <option value="Medium">Medium</option>
                                                            <option value="High">High</option>
                                                            <option value="Critical">Critical</option>
                                                        </select>
                                                        <ChevronDown size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" />
                                                    </div>
                                                </div>

                                                {/* NEW: Status Field (Only when editing) */}
                                                {editingGapId && (
                                                    <div>
                                                        <label className="block text-sm font-bold text-slate-700 mb-2">Status</label>
                                                        <div className="relative">
                                                            <select className="w-full p-3.5 pr-10 appearance-none rounded-xl border-0 ring-1 ring-slate-300 focus:ring-2 focus:ring-indigo-500 bg-white shadow-sm transition-all text-sm font-medium text-slate-800 cursor-pointer" value={gapData.status} onChange={e => setGapData({...gapData, status: e.target.value})}>
                                                                <option value="Open">Open</option>
                                                                <option value="In Review">In Review</option>
                                                                <option value="Resolved">Resolved</option>
                                                            </select>
                                                            <ChevronDown size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" />
                                                        </div>
                                                    </div>
                                                )}

                                                <div>
                                                    <label className="block text-sm font-bold text-slate-700 mb-2">Assign To</label>
                                                    <input type="text" className="w-full p-3.5 rounded-xl border-0 ring-1 ring-slate-300 focus:ring-2 focus:ring-indigo-500 bg-white shadow-sm transition-all text-sm font-medium text-slate-800 placeholder:text-slate-400" placeholder="e.g. John Doe" value={gapData.assignTo} onChange={e => setGapData({...gapData, assignTo: e.target.value})} required />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-bold text-slate-700 mb-2">Due Date</label>
                                                    <input type="date" className="w-full p-3.5 rounded-xl border-0 ring-1 ring-slate-300 focus:ring-2 focus:ring-indigo-500 bg-white shadow-sm transition-all text-sm font-medium text-slate-800" value={gapData.dueDate} onChange={e => setGapData({...gapData, dueDate: e.target.value})} required />
                                                </div>
                                            </div>

                                            <div className="pt-4 flex justify-end border-t border-slate-200 mt-6">
                                                <button type="submit" className="flex gap-2 items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-md active:scale-95">
                                                    <Save size={18} /> {editingGapId ? 'Update Risk' : 'Submit Risk'}
                                                    {/* <Save size={18} /> {editingGapId ? 'Update Gap' : 'Submit Gap'} */}
                                                </button>
                                            </div>
                                        </form>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* --- SIDEBAR --- */}
                <Card className="lg:col-span-4 bg-white rounded-[2rem] ring-1 ring-slate-200/60 shadow-sm overflow-hidden flex flex-col min-h-[500px] p-7" >
                    <CardHeader title="Review Timeline" subtitle="" icon={Clock} colorClass="bg-emerald-50 text-emerald-600" />
                    <div className="p-6 sm:p-8 flex-1">
                        {remarks && remarks.length > 0 ? (
                            <div className="relative">
                                <div className="absolute top-4 bottom-0 left-[19px] w-0.5 bg-slate-100 rounded-full"></div>
                                <div className="space-y-8">
                                    {remarks?.map((remark, index) => (
                                        <div key={index} className="relative pl-12 animate-in fade-in slide-in-from-bottom-4" style={{ animationDelay: `${index * 100}ms` }}>
                                            <div className="absolute left-0 top-1 w-10 h-10 rounded-full bg-white ring-4 ring-white flex items-center justify-center ring-1 ring-slate-200 shadow-sm z-10">
                                                <span className="text-sm font-black text-slate-700 uppercase">{remark.reviewerName.charAt(0)}</span>
                                            </div>
                                            <div className="bg-slate-50 rounded-2xl rounded-tl-none p-4 ring-1 ring-slate-200/60 hover:ring-indigo-200 transition-all">
                                                <div className="flex flex-col mb-2">
                                                    <p className="text-sm font-black text-slate-900 line-clamp-1">{remark.reviewerName}</p>
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">
                                                        {new Date(remark.date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                    </p>
                                                </div>
                                                <p className="text-sm text-slate-600 font-medium leading-relaxed">{remark.text}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-center opacity-70 ">
                                <MessageSquare className="w-12 h-12 text-slate-200 mb-4" />
                                <p className="text-sm font-bold text-slate-500">No review history yet.</p>
                                <p className="text-xs font-medium text-slate-400 mt-1 max-w-[200px]">Comments and actions will appear here once reviewed.</p>
                            </div>
                        )}
                    </div>
                </Card>
            </div>

            {/* Modals */}
            {pendingStatusChange && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-slate-900/40 backdrop-blur-sm transition-opacity">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md relative z-10 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        <div className="p-6 sm:p-8 text-center">
                            <div className="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-5 ring-8 ring-indigo-50/50">
                                <ShieldCheck size={32} strokeWidth={2} />
                            </div>
                            <h3 className="text-2xl font-black text-slate-900 mb-2">Confirm Status Change</h3>
                            <p className="text-sm font-medium text-slate-500 leading-relaxed">
                                Are you sure you want to change the status of this control to <span className="font-bold text-slate-800 uppercase">{pendingStatusChange}</span>?
                            </p>
                        </div>
                        <div className="p-6 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row gap-3">
                            <button onClick={cancelStatusChange} className="flex-1 px-4 py-3 text-sm font-bold text-slate-700 bg-white ring-1 ring-slate-200 rounded-xl hover:bg-slate-50 transition-colors shadow-sm">Cancel</button>
                            <button onClick={confirmStatusChange} className="flex-1 px-4 py-3 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 shadow-md shadow-indigo-600/20 active:scale-95 transition-all">Yes, Update Status</button>
                        </div>
                    </div>
                </div>
            )}

            {noteToDelete && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-slate-900/40 backdrop-blur-sm transition-opacity">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md relative z-10 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        <div className="p-6 sm:p-8 text-center">
                            <div className="w-16 h-16 bg-rose-50 text-rose-600 rounded-full flex items-center justify-center mx-auto mb-5 ring-8 ring-rose-50/50">
                                <AlertTriangle size={32} strokeWidth={2} />
                            </div>
                            <h3 className="text-2xl font-black text-slate-900 mb-2">Delete Note?</h3>
                            <p className="text-sm font-medium text-slate-500 leading-relaxed">Are you sure you want to delete this personal note? This action cannot be undone.</p>
                        </div>
                        <div className="p-6 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row gap-3">
                            <button onClick={() => setNoteToDelete(null)} className="flex-1 px-4 py-3 text-sm font-bold text-slate-700 bg-white ring-1 ring-slate-200 rounded-xl hover:bg-slate-50 transition-colors shadow-sm">Cancel</button>
                            <button onClick={executeDeleteNote} className="flex-1 px-4 py-3 text-sm font-bold text-white bg-rose-600 rounded-xl hover:bg-rose-700 shadow-md shadow-rose-600/20 active:scale-95 transition-all">Yes, Delete</button>
                        </div>
                    </div>
                </div>
            )}

            <RequestChangesModal setPageIsLoading={setPageIsLoading} pageIsLoading={pageIsLoading} isOpen={isRemarkModalOpen} onClose={() => setIsRemarkModalOpen(false)} onSubmit={handleRequestChanges} />
            <FilePreviewModal setPageIsLoading={setPageIsLoading} pageIsLoading={pageIsLoading} isOpen={isPreviewModalOpen} onClose={() => setIsPreviewModalOpen(false)} file={file} />
        </div>
    );
};

export default ControlDetailView;